import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { treatmentApi } from 'src/api/treatment';
import { translateKey } from 'src/locales/translate';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import { clinicalWriteRefusal } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import {
  AddProfileTreatmentDto,
  GetProfileTreatmentDto,
  UpdateProfileTreatmentDto,
} from 'woofwoof-api';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import { ScopedPage, sliceInMemory } from './scopedPage';
import {
  clinicalQueryKey,
  useClinicalCacheScope,
  useClinicalQuery,
} from './useClinicalQuery';
import { ScopeMutationKind } from './useRemindersScope';
import {
  useTreatmentCreateSeries,
  useTreatmentEditSeries,
} from './useTreatmentWriteSeries';
import { useVisitScope } from './useVisitScope';

interface TreatmentsScopeOptions {
  page: number;
  rowsPerPage: number;
  onMutationSuccess?: (
    kind: ScopeMutationKind,
    variables: AddProfileTreatmentDto | undefined,
  ) => void;
  onMutationError?: (kind: ScopeMutationKind, error: unknown) => void;
  /** The treatment the form shows, so the edit's unconfirmed state is its own (R3-4). */
  editingId?: string;
}

export interface TreatmentsScope extends ScopedPage<GetProfileTreatmentDto> {
  isLoading: boolean;
  isVisitScoped: boolean;
  /** `shown`: what the form showed, shown again if it reopens unconfirmed. */
  create: (
    payload: AddProfileTreatmentDto,
    shown?: TreatmentFormSnapshot,
  ) => void;
  update: (
    treatmentId: string,
    payload: UpdateProfileTreatmentDto,
    shown?: TreatmentFormSnapshot,
  ) => void;
  remove: (treatmentId: string) => void;
  /**
   * Every treatment in scope, not just the page on screen — what
   * select-across-pages and the PDF export need.
   */
  fetchAll: () => Promise<GetProfileTreatmentDto[]>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  /** R3-4: the last create may have committed unseen; the form must resend it. */
  createPending: boolean;
  /** R3-4: the same for the last edit of the treatment `editingId` names. */
  updatePending: boolean;
  /** F-8: the create series is held by 422 WAREHOUSE_DISABLED. */
  createBlocked: boolean;
  /** F-8: the same for the edit series. */
  updateBlocked: boolean;
  /** While pending: what the form showed when the frozen create was sent. */
  createFrozenValues?: TreatmentFormSnapshot;
  /** While pending: the same for the frozen edit. */
  updateFrozenValues?: TreatmentFormSnapshot;
}

/**
 * Treatments for the current scope — the patient's profile when there is one,
 * otherwise this visit alone. See `useVisitScope`.
 *
 * The visit branch reuses `['visit-treatments', visitId]`, the key
 * `VisitCourseCard` already writes. Every key is the selected organization's
 * (an owner's scope of its own): the answers carry its materials (R1-1).
 */
export function useTreatmentsScope({
  page,
  rowsPerPage,
  onMutationSuccess,
  onMutationError,
  editingId,
}: TreatmentsScopeOptions): TreatmentsScope {
  const { profileId, visitId, isVisitScoped } = useVisitScope();
  const queryClient = useQueryClient();
  const onStockWrite = useStockWriteFeedback();
  const isFacility = Boolean(useOrganizationContext().selectedOrganization?.id);
  const { scope, requestOptions } = useClinicalCacheScope();

  const profileQuery = useClinicalQuery(
    'profile-treatments',
    profileId,
    [page, rowsPerPage],
    (options) =>
      treatmentApi.treatmentRecordControllerGetProfileTreatments(
        { profileId, page, limit: rowsPerPage },
        options,
      ),
    {
      retry: 1,
      enabled: !isVisitScoped && Boolean(profileId),
      keepPreviousData: true,
    },
  );

  const visitQuery = useClinicalQuery(
    'visit-treatments',
    visitId,
    [],
    (options) =>
      treatmentApi.treatmentRecordControllerListVisitTreatments(
        { visitId: visitId || '' },
        options,
      ),
    { retry: 1, enabled: isVisitScoped && Boolean(visitId) },
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries(['profile-treatments', profileId]);
    // Adding a treatment can spawn medications and reminders, so both
    // neighbouring lists go stale with it.
    queryClient.invalidateQueries(['profile-reminders', profileId]);
    queryClient.invalidateQueries(['patient-medications', profileId]);
    if (visitId) {
      queryClient.invalidateQueries(['visit-treatments', visitId]);
      queryClient.invalidateQueries(['visit-reminders', visitId]);
      queryClient.invalidateQueries(['visit-medications', visitId]);
      queryClient.invalidateQueries(['visit-activity', visitId]);
    }
  }, [queryClient, profileId, visitId]);

  const fail = (kind: ScopeMutationKind) => (error: unknown) =>
    onMutationError?.(kind, error);

  // R3-4: a clinic's create and edit travel in write series, with or without
  // stock; an unresolved one resends its frozen request. The endpoint: the
  // patient's when there is one, the visit's otherwise.
  const route = patientOrVisitRoute(profileId, visitId);
  const createSeries = useTreatmentCreateSeries({
    route,
    onSuccess: (_response, body) => {
      invalidate();
      onMutationSuccess?.('create', body);
    },
    onError: fail('create'),
  });
  const updateSeries = useTreatmentEditSeries({
    route,
    editingId: editingId ?? '',
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('update', undefined);
    },
    onError: fail('update'),
  });

  // An owner has no organization and no Warehouse, and the backend keys no
  // owner write, so the owner's own pet page writes to the patient directly.
  // A visit without a patient has no such route: its treatments are its
  // clinic's, so with no clinic selected the write is refused, never posted
  // to `/api/profile//treatments`.
  const ownerWrite = !isFacility && !isVisitScoped;
  const refuse = (kind: ScopeMutationKind): void =>
    fail(kind)(
      clinicalWriteRefusal(
        translateKey('warehouse.clinical.visitWriteNeedsClinic'),
      ),
    );
  const { mutate: ownerCreate, isLoading: isOwnerCreating } = useMutation({
    mutationFn: (payload: AddProfileTreatmentDto): Promise<unknown> =>
      treatmentApi.treatmentRecordControllerAddProfileTreatment({
        profileId,
        addProfileTreatmentDto: { ...payload, visitId: visitId || undefined },
      }),
    onSuccess: (_data, variables) => {
      invalidate();
      onMutationSuccess?.('create', variables);
    },
    onError: fail('create'),
  });

  const { mutate: ownerUpdate, isLoading: isOwnerUpdating } = useMutation({
    mutationFn: ({
      treatmentId,
      payload,
    }: {
      treatmentId: string;
      payload: UpdateProfileTreatmentDto;
    }): Promise<unknown> =>
      treatmentApi.treatmentRecordControllerUpdateProfileTreatment({
        profileId,
        treatmentId,
        updateProfileTreatmentDto: {
          ...payload,
          visitId: visitId || undefined,
        },
      }),
    onSuccess: () => {
      invalidate();
      onMutationSuccess?.('update', undefined);
    },
    onError: fail('update'),
  });

  const { mutate: deleteMutate, isLoading: isDeleting } = useMutation({
    mutationFn: (treatmentId: string): Promise<unknown> =>
      isVisitScoped
        ? treatmentApi.treatmentRecordControllerRemoveVisitTreatment({
            visitId: visitId || '',
            treatmentRecordId: treatmentId,
          })
        : treatmentApi.treatmentRecordControllerDeleteProfileTreatment({
            profileId,
            treatmentId,
            visitId: visitId || undefined,
          }),
    // A delete reverses the treatment's stock.
    onSuccess: (response) => {
      invalidate();
      onStockWrite(response);
      onMutationSuccess?.('delete', undefined);
    },
    onError: fail('delete'),
  });

  const fetchAll = useCallback(
    () =>
      queryClient.fetchQuery(
        isVisitScoped
          ? clinicalQueryKey('visit-treatments-all', visitId, scope)
          : clinicalQueryKey('profile-treatments-all', profileId, scope),
        async ({ signal }) => {
          // Asked without paging parameters, the profile endpoint returns the
          // whole list; the visit endpoint has only ever returned it whole.
          const options = requestOptions(signal);
          const response = isVisitScoped
            ? await treatmentApi.treatmentRecordControllerListVisitTreatments(
                { visitId: visitId || '' },
                options,
              )
            : await treatmentApi.treatmentRecordControllerGetProfileTreatments(
                { profileId },
                options,
              );
          const payload = response.data as any;
          return (payload?.data ?? payload) as GetProfileTreatmentDto[];
        },
      ),
    [queryClient, isVisitScoped, visitId, profileId, scope, requestOptions],
  );

  const paged: ScopedPage<GetProfileTreatmentDto> = isVisitScoped
    ? sliceInMemory(visitQuery.data?.data ?? [], page, rowsPerPage)
    : {
        items: profileQuery.data?.data.data ?? [],
        total: profileQuery.data?.data.total ?? 0,
      };

  return {
    ...paged,
    isLoading: isVisitScoped ? visitQuery.isLoading : profileQuery.isLoading,
    isVisitScoped,
    create: (payload, shown) => {
      if (isFacility) createSeries.submit(null, payload, shown);
      else if (ownerWrite) ownerCreate(payload);
      else refuse('create');
    },
    update: (treatmentId, payload, shown) => {
      if (isFacility) updateSeries.submit(treatmentId, payload, shown);
      else if (ownerWrite) ownerUpdate({ treatmentId, payload });
      else refuse('update');
    },
    remove: deleteMutate,
    fetchAll,
    isCreating: createSeries.isLoading || isOwnerCreating,
    isUpdating: updateSeries.isLoading || isOwnerUpdating,
    isDeleting,
    createPending: createSeries.pending,
    updatePending: updateSeries.pending,
    createBlocked: createSeries.blocked,
    updateBlocked: updateSeries.blocked,
    createFrozenValues: createSeries.frozenValues,
    updateFrozenValues: updateSeries.frozenValues,
  };
}
